import React, { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Container,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Logo } from '../components/Logo';
import axios from '../api/axios';

export const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const validatePassword = (value: string): boolean => {
    if (!value) {
      setPasswordError(t('auth.login.passwordRequired'));
      return false;
    }
    if (value.length < 6) {
      setPasswordError(t('auth.login.passwordMin'));
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value: string): boolean => {
    if (!value) {
      setConfirmPasswordError(t('auth.register.confirmPasswordRequired'));
      return false;
    }
    if (value !== password) {
      setConfirmPasswordError(t('auth.register.passwordsMismatch'));
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const isPasswordValid = validatePassword(password);
    const isConfirmValid = validateConfirmPassword(confirmPassword);

    if (!isPasswordValid || !isConfirmValid) {
      return;
    }

    setLoading(true);

    try {
      await axios.post('/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.resetPassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #F5F5F0 0%, #E8E8DC 100%)',
        py: 4,
      }}
    >
      <Box sx={{ position: 'fixed', top: 24, right: 24 }}>
        <LanguageSwitcher />
      </Box>
      <Container maxWidth="sm">
        <Card
          sx={{
            boxShadow: '0 8px 32px rgba(74, 124, 89, 0.1)',
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Logo size="large" />
              <Typography
                variant="h5"
                sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'text.primary' }}
              >
                {t('auth.resetPassword.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('auth.resetPassword.subtitle')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success ? (
              <>
                <Alert severity="success" sx={{ mb: 3 }}>
                  {t('auth.resetPassword.success')}
                </Alert>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #4A7C59 0%, #355A40 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #355A40 0%, #2A4833 100%)',
                    },
                  }}
                >
                  {t('auth.resetPassword.goToLogin')}
                </Button>
              </>
            ) : (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label={t('auth.resetPassword.newPassword')}
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) validatePassword(e.target.value);
                  }}
                  onBlur={() => validatePassword(password)}
                  error={!!passwordError}
                  helperText={passwordError}
                  margin="normal"
                  required
                  autoComplete="new-password"
                  autoFocus
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label={t('auth.resetPassword.confirmPassword')}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (confirmPasswordError) validateConfirmPassword(e.target.value);
                  }}
                  onBlur={() => validateConfirmPassword(confirmPassword)}
                  error={!!confirmPasswordError}
                  helperText={confirmPasswordError}
                  margin="normal"
                  required
                  autoComplete="new-password"
                  disabled={loading}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #4A7C59 0%, #355A40 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #355A40 0%, #2A4833 100%)',
                    },
                  }}
                >
                  {loading ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
                </Button>
              </form>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('auth.forgotPassword.backToLogin')}
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ResetPassword;
